import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import AuthenticationService from '../service/AuthenticationService.js';
import AuthLayout, { PasswordInput, FieldError, apiErrorMessage, isValidEmail } from '../components/AuthLayout';

// Same limits as sign-up (backend SignUpRequest).
const MIN_PASSWORD = 6
const MAX_PASSWORD = 20

class ForgotPassword extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            resetCode: '',
            email: '',
            password: '',
            confirmPassword: '',
            errorMsg: '',
            fieldErrors: {},
            isLoading: false,
            isResetPage: false
        };
    }

    onChange = (key) => (event) => {
        this.setState({ [key]: event.target.value })
    }

    resendPage = () => {
        this.setState({
            isResetPage: false,
            errorMsg: '',
            fieldErrors: {},
            resetCode: ''
        })
    }

    sendResetCode = (event) => {
        event.preventDefault();
        const email = this.state.email.trim()
        if (!isValidEmail(email)) {
            this.setState({ fieldErrors: { email: 'Please enter a valid email address.' }, errorMsg: '' })
            return
        }
        this.setState({
            isLoading: true,
            fieldErrors: {},
            errorMsg: ''
        })
        AuthenticationService.sendResetCode(email).then(response => {
            if (response.status === 200) {
                this.setState({
                    email,
                    isLoading: false,
                    isResetPage: true
                })
            } else {
                this.setState({
                    isLoading: false,
                    errorMsg: 'Could not send the reset code. Please try again.'
                })
            }
        }).catch(error => {
            this.setState({
                errorMsg: apiErrorMessage(error, 'Could not send the reset code. Please try again.'),
                isLoading: false
            })
        })
    }

    verifyAndUpdatePassword = (event) => {
        event.preventDefault();
        const { email, resetCode, password, confirmPassword } = this.state
        const fieldErrors = {}
        if (!resetCode.trim()) fieldErrors.code = 'Please enter the code from your email.'
        if (password.length < MIN_PASSWORD || password.length > MAX_PASSWORD) fieldErrors.password = `Password must be ${MIN_PASSWORD} to ${MAX_PASSWORD} characters.`
        if (!fieldErrors.password && password !== confirmPassword) fieldErrors.confirmPassword = 'Passwords do not match.'
        this.setState({ fieldErrors, errorMsg: '' })
        if (Object.keys(fieldErrors).length > 0) return

        this.setState({
            isLoading: true
        })
        AuthenticationService.verifyAndUpdatePassword(email, resetCode.trim(), password).then(response => {
            if (response.status === 200) {
                this.setState({
                    isLoading: false
                })
                this.props.history.push({ pathname: "/login", state: { passwordReset: true, email } })
            } else {
                this.setState({
                    isLoading: false,
                    errorMsg: 'Could not update your password. Please try again.'
                })
            }
        }).catch(error => {
            this.setState({
                errorMsg: apiErrorMessage(error, 'That code did not work. Please check it and try again.'),
                isLoading: false
            })
        })
    }

    render() {
        const { email, password, confirmPassword, isLoading, errorMsg, fieldErrors, isResetPage, resetCode } = this.state
        const footer = <>Remembered it? <Link className='mv-auth-link' to='/login'>Back to log in</Link></>

        if (!isResetPage) {
            return (
                <AuthLayout title='Forgot your password?'
                    subtitle='Enter your email and we will send you a code to reset it.'
                    footer={footer}>
                    <form onSubmit={this.sendResetCode} className='mv-auth-form' noValidate>
                        {errorMsg && <div className='mv-alert mv-alert-error' role='alert'>{errorMsg}</div>}
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='reset-email'>Email</label>
                            <input id='reset-email' className='mv-input' type='email' inputMode='email' autoComplete='email'
                                placeholder='you@example.com' value={email} onChange={this.onChange('email')}
                                aria-invalid={fieldErrors.email ? 'true' : undefined}
                                aria-describedby={fieldErrors.email ? 'reset-email-error' : undefined} />
                            <FieldError id='reset-email-error' message={fieldErrors.email} />
                        </div>
                        <button type='submit' className={`mv-btn mv-btn-primary mv-btn-block mv-auth-submit${isLoading ? ' is-busy' : ''}`} disabled={isLoading} aria-busy={isLoading}>
                            {isLoading ? 'Sending code…' : 'Send reset code'}
                        </button>
                    </form>
                </AuthLayout>
            )
        }

        return (
            <AuthLayout title='Set a new password'
                subtitle='We have emailed you a reset code. Enter it below with your new password.'
                footer={footer}>
                <form onSubmit={this.verifyAndUpdatePassword} className='mv-auth-form' noValidate>
                    {errorMsg && <div className='mv-alert mv-alert-error' role='alert'>{errorMsg}</div>}

                    <div className='mv-auth-email'>
                        <span>{email}</span>
                        <button type='button' className='mv-auth-link' onClick={this.resendPage}>Resend code</button>
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='reset-code'>Reset code</label>
                        <input id='reset-code' className='mv-input mv-code-input' inputMode='numeric' autoComplete='one-time-code'
                            maxLength={5} value={resetCode} onChange={this.onChange('resetCode')}
                            aria-invalid={fieldErrors.code ? 'true' : undefined}
                            aria-describedby={fieldErrors.code ? 'reset-code-error' : undefined} />
                        <FieldError id='reset-code-error' message={fieldErrors.code} />
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='reset-password'>New password</label>
                        <PasswordInput id='reset-password' value={password} onChange={this.onChange('password')}
                            placeholder={`${MIN_PASSWORD} to ${MAX_PASSWORD} characters`} autoComplete='new-password'
                            invalid={!!fieldErrors.password} describedBy={fieldErrors.password ? 'reset-password-error' : undefined} />
                        <FieldError id='reset-password-error' message={fieldErrors.password} />
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='reset-confirm'>Confirm new password</label>
                        <PasswordInput id='reset-confirm' value={confirmPassword} onChange={this.onChange('confirmPassword')}
                            placeholder='Type it again' autoComplete='new-password'
                            invalid={!!fieldErrors.confirmPassword} describedBy={fieldErrors.confirmPassword ? 'reset-confirm-error' : undefined} />
                        <FieldError id='reset-confirm-error' message={fieldErrors.confirmPassword} />
                    </div>

                    <button type='submit' className={`mv-btn mv-btn-primary mv-btn-block mv-auth-submit${isLoading ? ' is-busy' : ''}`} disabled={isLoading} aria-busy={isLoading}>
                        {isLoading ? 'Updating…' : 'Update password'}
                    </button>
                </form>
            </AuthLayout>
        );
    }
}

export default withRouter(ForgotPassword);
